import { useState, useEffect, useMemo } from 'react';
import { httpCall } from '../../../common/util';
import { normalizeAll } from '../normalizer';
import { compileRules, applyCompiledRules } from '../ruleEngine/compileRules';
import { DEFAULT_RULE_PROVIDERS } from '../ruleProviders';
import { mergeRule } from '../ruleEngine/ruleSchema';

export function useFieldConfig(
  templateId,
  renderLocation,
  manualRuleOverrides = {},
  rawRows = null,
  renderType = 'activity',
  ruleProviders = DEFAULT_RULE_PROVIDERS,
) {
  const [fetchedRows, setFetchedRows] = useState(null);
  const [isLoading,   setIsLoading]  = useState(false);
  const [error,       setError]      = useState(null);

  useEffect(() => {
    if (rawRows !== null) {
      setFetchedRows(rawRows);
    }
  }, [rawRows]);

  useEffect(() => {
    if (rawRows !== null) return;
    if (!templateId)     return;

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    fetchFieldConfigsFromApi(templateId, renderType)
      .then(rows => {
        if (!cancelled) setFetchedRows(rows);
      })
      .catch(err => {
        if (!cancelled) {
          console.error('[FieldEngine] Failed to fetch field configs:', err);
          setError('Failed to load form configuration.');
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, [templateId, renderLocation, rawRows, renderType]);

  const fieldConfigs = useMemo(() => {
    const rows = rawRows ?? fetchedRows ?? [];
    const base = normalizeAll(rows);
    const providerRuleMap = compileRules(base, ruleProviders);
    const finalRuleMap = mergeManualOverrides(providerRuleMap, manualRuleOverrides);
    return applyCompiledRules(base, finalRuleMap);
  }, [rawRows, fetchedRows, manualRuleOverrides, ruleProviders]);

  return { fieldConfigs, isLoading, error };
}

function mergeManualOverrides(providerMap, overrides) {
  if (!overrides || typeof overrides !== 'object') return providerMap;
  const keys = Object.keys(overrides);
  if (keys.length === 0) return providerMap;

  const out = { ...providerMap };
  for (const k of keys) {
    const manual = normalizeManualOverride(overrides[k]);
    out[k] = out[k] ? mergeRule(out[k], manual) : manual;
  }
  return out;
}

function normalizeManualOverride(entry) {
  if (!entry || typeof entry !== 'object') {
    return { effects: [], validation: [], optionFilter: null };
  }
  const validation = entry.validation !== undefined
    ? entry.validation
    : (entry.additionalValidation ?? []);
  return {
    effects:       entry.effects       ?? [],
    validation,
    optionFilter:  entry.optionFilter  ?? null,
    isNonEditable: entry.isNonEditable,
  };
}

async function fetchFieldConfigsFromApi(templateId, renderType) {
  const url = renderType === 'workkard' ? '/gwrkform' : '/cact';

  const response = await httpCall({
    method: 'GET',
    url,
    headers: { 'Content-Type': 'application/json' },
    params: { tmplId: templateId },
  });

  const payload = response?.model ?? response;

  if (Array.isArray(payload))                         return payload;
  if (payload?.CustomFields && Array.isArray(payload.CustomFields)) return payload.CustomFields;
  if (payload?.Data && Array.isArray(payload.Data))   return payload.Data;

  return [];
}
